import { action, computed, observable, set } from 'mobx';
import {
    IOpening,
    IOpeningRaw,
    IOpeningAttributes,
    IPhoneNumber,
    IStore as IStoreLocator,
    IStoresGeographical,
    ITag,
    IEvent,
    IHoliday
} from '../../../api/Interfaces';
import { IInStoreService, InStoreService } from './InStoreService';
import { Address } from './Address';
import { ISkusOnhand, DOOR_STATUS } from '~api/BOPISApiInterfaces';
import { fromMilitaryToStandardTime } from '../../../utils/TimeUtils';
import { Config } from '~domain/entities/Config';

export interface IStore extends IStoreLocator {
    canBookAppointments?: boolean;
}

export class Store {
    @observable public id: string;
    @observable public address: Address;
    @observable public geographical: IStoresGeographical;
    @observable public name: string;
    @observable public opening: IOpening[];
    @observable public openingHoursRaw: IOpeningRaw[];
    @observable public phoneNumbers: IPhoneNumber[];
    @observable public tags: ITag[];
    @observable public storeUrl?: string;
    @observable public showStoreDetails?: boolean;
    @observable public counterId: string;
    @observable public jdaStoreNumber: string;
    @observable public canBookAppointments: boolean = false;
    @observable public promotions: string;
    @observable public openingAttributes?: IOpeningAttributes;
    @observable public productIsAvailable?: boolean; // Used for BOPIS
    @observable public curbsideIsAvailable?: number;
    @observable public inStoreServices?: IInStoreService[];
    @observable public upcomingEventsNumber: number;
    @observable public events?: IEvent[];
    @observable public cartItems: { [key: string]: ISkusOnhand };
    @observable public skusOnhand: { [key: string]: ISkusOnhand };
    @observable public configService: Config;
    @observable public courierDelivery: DOOR_STATUS;
    @observable public config: Config;
    @observable public inStorePickup?: boolean;
    @observable public holidays?: IHoliday[];

    constructor(config: Config) {
        this.config = config;
    }

    public static fromJson(data: IStore, config: Config): Store {
        const store = new Store(config);
        set(store, data);

        const {
            opening_attributes,
            tags,
            address,
            phone_number,
            store_url,
            counter_id,
            jdaStoreNumber,
            showStoreDetails,
            services,
            in_store_pickup,
            holidays
        } = data;

        store.address = Address.fromJSON(address);
        store.phoneNumbers = phone_number;
        store.tags = (tags && tags.items) || [];
        store.storeUrl = store_url;
        store.showStoreDetails = showStoreDetails;
        store.counterId = counter_id;
        store.jdaStoreNumber = jdaStoreNumber;
        store.openingAttributes = opening_attributes;
        store.inStoreServices = services?.map((service) => InStoreService.fromJSON(service));
        store.inStorePickup = in_store_pickup;
        store.holidays = holidays;

        return store;
    }

    @action
    public update(data: Partial<IStore>) {
        set(this, data);
    }

    @computed
    private get getCurrentDayIndex() {
        return new Date().getDay();
    }

    @computed
    public get todayOpeningHours() {
        const opening = this.opening.find((item) => item.day_of_week === this.getCurrentDayIndex);
        if (opening) {
            return opening.open_hours[0];
        }
    }

    @computed
    public get primaryPhone() {
        return this.phoneNumbers.find(
            (item) => (item.label || '').toLowerCase() === 'main' && item.value !== ''
        );
    }

    @action
    private hoursWithOffset = (offset: number) => {
        const today = new Date();

        return new Date(today.setHours(today.getHours() + offset));
    };

    @computed
    get today(): number {
        const date = new Date();

        return date.getDay();
    }

    @computed
    public get isAvailableTomorrow() {
        const openHours = this.opening;
        const tomorrowIndex = this.today;
        const nextDayHours = openHours && openHours[tomorrowIndex];
        const isOpen =
            nextDayHours &&
            nextDayHours.open_hours[0].open !== '00:00' &&
            nextDayHours.open_hours[0].close !== '00:00';

        return isOpen;
    }

    @computed
    public get isAvailableTodayBopis() {
        const hours = this.getStoreHours;

        return this.isAvailableToday(hours.currentHoursWithBufferBopis);
    }

    @computed
    public get isAvailableTodayCourier() {
        const hours = this.getStoreHours;

        return this.isAvailableToday(hours.currentHoursWithBufferCourier);
    }

    @computed
    public get getStoreHours() {
        const courierOffset = this.config?.config.postmatesCutoffTimeOffset;
        const bopisOffset = this.config?.config.bopisDropoffOffset;
        const todayWithBuffer = this.hoursWithOffset(courierOffset);
        const todayWithBufferBopis = this.hoursWithOffset(bopisOffset);
        const currentHoursWithBufferCourier =
            todayWithBuffer.getHours() < courierOffset ? 24 : todayWithBuffer.getHours();
        const currentHoursWithBufferBopis =
            todayWithBufferBopis.getHours() < bopisOffset ? 24 : todayWithBufferBopis.getHours();
        const currentMinutesWithBuffer = todayWithBuffer.getMinutes();
        const workingHours = this.openingHoursRaw[this.getCurrentDayIndex];
        const closingHours = parseInt(workingHours?.close.toString().substring(0, 2), 10);
        const closingMinutes = parseInt(workingHours?.close.toString().substring(2, 4), 10);
        const lastOrder = fromMilitaryToStandardTime(
            Number(workingHours?.close) - courierOffset * 100
        );

        return {
            currentHoursWithBufferCourier,
            currentMinutesWithBuffer,
            currentHoursWithBufferBopis,
            closingHours,
            closingMinutes,
            lastOrder
        };
    }

    @action
    private isAvailableToday = (bufferHours: number) => {
        const hours = this.getStoreHours;
        if (hours) {
            if (
                hours.closingHours > bufferHours ||
                (hours.closingHours === bufferHours &&
                    hours.closingMinutes > hours.currentMinutesWithBuffer)
            ) {
                return true;
            }

            return false;
        }
    };

    @computed
    public get currentHoliday() {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const date = now.getDate();

        return this.holidays?.find((holiday) => {
            const holidayDate = new Date(holiday.date);

            return (
                holidayDate.getFullYear() === year &&
                holidayDate.getMonth() === month &&
                holidayDate.getDate() === date
            );
        });
    }
}
